import { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@mui/styles'
import LatestForecastPanel from '../panels/LatestForecastPanel'
import { setSelectedUserSetup } from '../redux/actions/userSetups'
import Api from '../api'
import { CircularProgress, Grid, Typography } from '@mui/material'
import NoSite from '../components/NoSite'
import MetricsTimeSeriesChart from '../charts/Metrics/MetricsTimeSeriesChart'
import moment from 'moment-timezone'
import { loadStateFromLocalStorage } from '../redux/utilsLocalStorage'

const useStyles = makeStyles(theme => ({
  centerItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  layoutChart: {
    marginTop: '30px',
  },
  label: {
    fontSize: 14,
    color: theme.palette.primary.main,
    // textTransform: 'uppercase',
  },
  value: {
    fontSize: 20,
    fontWeight: '400',
    // backgroundColor: 'lightgray',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'uppercase',
  },
}))

const convertUtcStringToTimezone = (utcString, timezone) => {
  return moment
    .tz(utcString, 'YYYY-MM-DD HH:mm', 'UTC')
    .tz(timezone)
    .format('YYYY-MM-DD HH:mm')
}

// Convert a UTC ISO 8601 string ("2026-07-09T10:10:00") to the user's timezone
const convertUtcIsoToTimezone = (utcIso, timezone) => {
  if (!utcIso) return ''
  return moment.utc(utcIso).tz(timezone).format('YYYY-MM-DD HH:mm')
}

const LatestForecast = props => {
  const classes = useStyles(props)
  const {
    container,
    selectedSetup,
    setSelectedUserSetup,
    unit,
    timezone,
    selectedSetupPlant,
    selectedForecastType,
    userPowerSetups,
  } = props

  // Use userPowerSetups from Redux, validate selectedSetup locally
  const userSetups = userPowerSetups
  const validatedSetup = userPowerSetups.includes(selectedSetup)
    ? selectedSetup
    : null

  // If there is only 1 available setup, select it automatically
  useEffect(() => {
    if (userSetups.length === 1 && selectedSetup !== userSetups[0]) {
      setSelectedUserSetup(userSetups[0])
    }
  }, [userSetups, selectedSetup, setSelectedUserSetup])

  const [loading, setLoading] = useState(false)
  const [latestForecastData, setLatestForecastData] = useState(null)
  // the selected_date sent with the current request (UTC datetime)
  const [requestedDate, setRequestedDate] = useState(null)
  // which mode the request was made in: 'latest' | 'date'
  const [requestedMode, setRequestedMode] = useState('latest')

  const disableButton = latestForecastData !== null || loading ? true : false

  // for disabling inputs in form
  const [trackDisableStatus, setTrackDisableStatus] = useState(false)

  const intervalRef = useRef(null) // keep reference to interval

  // use this timezone from store because of refreshing page
  const timezoneStore =
    loadStateFromLocalStorage('timezone') !== undefined
      ? loadStateFromLocalStorage('timezone')
      : 'Etc/UTC'

  let initNodata = {
    heading: 'No setup selected',
    body: 'Select a setup and adjust parameters in order to start latest forecast',
  }

  if (validatedSetup) {
    initNodata = {
      heading: '',
      body: '',
    }
  }

  const [nodataContent, setNodataContent] = useState(initNodata)

  const startLatestForecastData = async latestForecastObj => {
    setLoading(true)
    setTrackDisableStatus(true)
    try {
      const response =
        await Api.LatestForecast.latestForecast(latestForecastObj)
      if (response.status === 204) {
        const statusText = response.statusText || 'No data available'
        if (selectedForecastType === 'aggregated') {
          setLatestForecastData(null)
          setTrackDisableStatus(false)
          setNodataContent({
            heading: statusText || `No aggregated data — the dataset is empty`,
            body: '',
          })
        } else {
          // plant level
          setLatestForecastData(null)
          setTrackDisableStatus(false)
          setNodataContent({
            heading:
              statusText ||
              `No data for plant ${selectedSetupPlant || 'Unknown'}`,
            body: '',
          })
        }
      }

      if (response.status === 200) {
        const latestForecast = response?.data?.data

        if (!response.data || !latestForecast) {
          setNodataContent({
            heading: `Data issue for setup: ${selectedSetup}`,
            body: 'We received an invalid dataset from the server.',
          })
          setLatestForecastData(null)
          setTrackDisableStatus(false)
        } else {
          setLatestForecastData(response.data)
        }
      }

      setLoading(false)
    } catch (error) {
      setLatestForecastData(null)
      setTrackDisableStatus(false)
      if (error.response.status === 411) {
        setNodataContent({
          heading: `Error running query for selected setup: ${selectedSetup}`,
          body: '',
        })
      } else {
        setNodataContent({
          heading: `Error fetching data for selected setup: ${selectedSetup}`,
          body: '',
        })
      }
      console.error('Error fetching Latest Forecast data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleStartLatestForecast = (latestForecastObj, mode = 'latest') => {
    // remember which date/mode was requested so the result panel can label it
    setRequestedDate(latestForecastObj?.selected_date ?? null)
    setRequestedMode(mode)

    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    startLatestForecastData(latestForecastObj)

    // Start interval for every 5 minutes (300000 ms)
    intervalRef.current = setInterval(() => {
      startLatestForecastData(latestForecastObj)
    }, 300000)
  }

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  const handleNoDataContentUpdate = newContent => {
    setNodataContent(newContent)
    setLatestForecastData(null)
  }

  /** This code will activate reset metricsData if user change the unit or timezone inside Logout component */
  useEffect(() => {
    setLatestForecastData(null)
  }, [timezone, unit])

  const resultPanel = () => {
    if (latestForecastData === null) {
      return (
        <NoSite
          {...props}
          creating={false}
          heading={nodataContent.heading}
          body={nodataContent.body}
        ></NoSite>
      )
    }

    // Latest mode keeps the "Latest Update" label; Date mode shows the requested time
    const isLatestRequest = requestedMode === 'latest'

    return (
      <Grid container spacing={0} className={classes.layoutChart}>
        <Grid item md={12}>
          <Typography className={classes.label}>
            {isLatestRequest
              ? 'Latest Update'
              : `Closest available to ${convertUtcIsoToTimezone(
                  requestedDate,
                  timezoneStore,
                )}`}
          </Typography>
        </Grid>

        <Grid item md={12}>
          <Typography className={classes.value}>
            {convertUtcStringToTimezone(
              latestForecastData['latest_update'],
              timezoneStore,
            )}
          </Typography>
        </Grid>

        <Grid item md={12}>
          <MetricsTimeSeriesChart
            chartDataRaw={latestForecastData['data']}
            resolution={latestForecastData.resolution}
            chartName={'chartLatestForecastTimeSeries'}
            selectedSetup={selectedSetup}
            selectedPlant={selectedSetupPlant}
            latestUpdate={latestForecastData['latest_update']}
            setTrackDisableStatus={setTrackDisableStatus}
            chartType={'latest'}
          />
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      <LatestForecastPanel
        container={container}
        userSetups={userSetups}
        setLatestForecastParameters={handleStartLatestForecast}
        handleNoDataContentUpdate={handleNoDataContentUpdate}
        setLatestForecastData={setLatestForecastData}
        disableButton={disableButton}
        trackDisableStatus={trackDisableStatus}
      >
        {!loading ? (
          resultPanel()
        ) : (
          <div className={classes.centerItem}>
            <CircularProgress />
          </div>
        )}
      </LatestForecastPanel>
    </>
  )
}

const mapStateToProps = state => ({
  selectedSetup: state.userSetups.selectedSetup,
  userPowerSetups: state.userSetups.userPowerSetups,
  unit: state.user.unit,
  timezone: state.user.timezone,
  selectedSetupPlant: state.metrics.selectedPlant,
  selectedForecastType: state.metrics.selectedForecast.name,
})

const mapDispatchToProps = {
  setSelectedUserSetup,
}

export default connect(mapStateToProps, mapDispatchToProps)(LatestForecast)
